import { HARD_REFRESH_FETCH_TIMEOUT_MS } from '../constants/bookingConst';

/**
 * Hard-refreshes the page: drops anything the browser could serve stale, then
 * reloads — either the current page or, when `targetUrl` is given, that page
 * instead (e.g. the booking grid from the connection-error screen).
 *
 * Why not just location.reload(): browsers have no scriptable "hard reload"
 * (the forceGet flag is Firefox-only and non-standard). A normal reload can
 * be served index.html from the HTTP cache or a service worker, which is
 * exactly the copy that may be broken. So this:
 *  1. unregisters any service worker,
 *  2. empties Cache Storage,
 *  3. re-fetches the destination page with `cache: 'reload'`, which bypasses the
 *     HTTP cache and overwrites its entry with a fresh copy,
 *  4. reloads (or navigates to the target) — now picking up that fresh copy. The JS/CSS bundles are
 *     content-hashed by the build, so a fresh index.html pulls fresh assets.
 *
 * Every cleanup step is best-effort: a failure (offline, storage blocked,
 * API missing) must never stop the reload itself.
 *
 * @param {string} [targetUrl] - page to land on after the caches are cleared,
 *   absolute or relative to the current page. Omitted: the current page.
 * @returns {Promise<void>} resolves just before the page unloads
 */
export const hardRefreshPage = async (targetUrl) => {
  const currentUrl = window.location.href;
  let destination = currentUrl;
  try {
    if (targetUrl) destination = new URL(targetUrl, currentUrl).href;
  } catch {
    // Malformed target — fall back to reloading the current page.
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Not fatal — continue to the reload.
  }

  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Not fatal — continue to the reload.
  }

  // Capped: on a dead connection this fetch can otherwise hang for minutes,
  // leaving the guest staring at a button that seems to do nothing.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const abortTimer = controller
    ? window.setTimeout(() => controller.abort(), HARD_REFRESH_FETCH_TIMEOUT_MS)
    : null;
  try {
    await fetch(destination, {
      cache: 'reload',
      credentials: 'same-origin',
      signal: controller?.signal,
    });
  } catch {
    // Offline, aborted or blocked — the reload below still runs.
  } finally {
    if (abortTimer) window.clearTimeout(abortTimer);
  }

  if (destination === currentUrl) {
    window.location.reload();
    return;
  }
  // replace(), not assign(): Back should not return the guest to the page
  // that just failed to load.
  window.location.replace(destination);
};

export default hardRefreshPage;
